import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import { IconBookUpload } from "@tabler/icons-react"
import type React from "react"
import { useState } from "react"
import AddVocabularyFilterForm, {
  type AddVocabularyFilterValues,
} from "./components/AddVocabularyFilterForm"
import AddVocabularyTable, {
  type AddVocabularyLogItem,
} from "./components/AddVocabularyTable"
import { AddVocabularyDetailModal } from "./components/AddVocabularyDetailModal"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"
import { logService } from "@/services/logApi"

export interface AddVocabularyLogQueryParams {
  page: number
  per_page: number
  search?: string
  source_type?: string
  status?: string
  start_date?: string
  end_date?: string
}

export const AddVocabularyLogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<AddVocabularyLogQueryParams>({
    ...PAGE_CONFIG,
    search: "",
    source_type: undefined,
    status: undefined,
    start_date: undefined,
    end_date: undefined,
  })

  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<AddVocabularyLogItem | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["add_vocabulary_logs_list", searchParams],
    queryFn: () =>
      logService.listAddVocabularyLogs({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search ? searchParams.search.trim() : undefined,
        source_type: searchParams.source_type || undefined,
        status: searchParams.status || undefined,
        start_date: searchParams.start_date || undefined,
        end_date: searchParams.end_date || undefined,
      }),
  })

  const handleSearch = (values: AddVocabularyFilterValues) => {
    setSearchParams({
      ...searchParams,
      search: values.search,
      source_type: values.source_type,
      status: values.status,
      start_date: values.start_date,
      end_date: values.end_date,
      page: 1,
    })
  }

  const items = (data?.data || []) as AddVocabularyLogItem[]
  const totalCount = data?.total ?? 0

  return (
    <div className="flex flex-col gap-2">
      <AddVocabularyFilterForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          source_type: searchParams.source_type,
          status: searchParams.status,
          start_date: searchParams.start_date,
          end_date: searchParams.end_date,
        }}
      />

      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <IconBookUpload className="text-blue-500" size={20} />
              <span className="font-semibold text-base text-gray-800">
                Log Thêm Từ Vựng Hàng Loạt (Add Vocabulary Logs)
              </span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số lượt: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button
              icon={<ReloadOutlined spin={isFetching} />}
              onClick={() => refetch()}
              loading={isFetching}
            >
              Làm mới
            </Button>
          </div>
        }
      >
        <AddVocabularyTable
          data={items}
          loading={isFetching}
          onViewDetail={(record, index) => {
            setSelectedRecord(record)
            setSelectedIndex(index)
            setDetailModalOpen(true)
          }}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      <AddVocabularyDetailModal
        open={detailModalOpen}
        record={selectedRecord}
        items={items}
        currentIndex={selectedIndex}
        onNavigate={(newIndex) => {
          setSelectedIndex(newIndex)
          setSelectedRecord(items[newIndex] || null)
        }}
        onClose={() => {
          setDetailModalOpen(false)
          setSelectedRecord(null)
          setSelectedIndex(0)
        }}
      />
    </div>
  )
}

export default AddVocabularyLogPage
